import { Box, Stack, Typography } from '@mui/joy';
import { useTranslation } from 'react-i18next';

import { LIST_HEIGHT } from 'contacts/components/VendorSearch/constants';
import { IContactGroupAddress, IVendor } from 'contacts/types';
import { InfiniteScroll } from 'core/components';
import { Loader } from 'core/elements';
import { Level } from 'theme/enums';

import { AlternateVendorCard } from './AlternateVendorCard';
import { LegacyVendorCard } from './LegacyVendorCard';

type VendorRecord = IContactGroupAddress | IVendor;

interface IProps {
  records: VendorRecord[];
  count: number;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  isLoading: boolean;
  selectedVendorId?: number;
  onVendorInfo: (vendor: IContactGroupAddress) => void;
  isAlternateVersionEnabled?: boolean;
  minCharsToSearch?: number;
  query?: string;
}

export const VendorList = ({
  records,
  count,
  hasNextPage,
  fetchNextPage,
  isLoading,
  selectedVendorId,
  onVendorInfo,
  isAlternateVersionEnabled,
  minCharsToSearch = 0,
  query,
}: IProps) => {
  const { t } = useTranslation();

  if ((query?.length ?? 0) < minCharsToSearch) {
    return (
      <Typography level={Level.BODY_SM} sx={{ color: 'neutral.500' }}>
        {t('messageSearchByCharsLength', { length: minCharsToSearch })}
      </Typography>
    );
  }

  if (isLoading) {
    return <Loader />;
  }

  return (
    <Stack sx={{ gap: 1 }}>
      <Typography level={Level.BODY_SM} sx={{ color: 'neutral.500' }}>
        {count ? t('labelResults', { count }) : t('labelNoResults')}
      </Typography>

      <Box>
        <InfiniteScroll
          data={records}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          height="fit-content"
          style={{
            overflowX: 'hidden',
            maxHeight: `calc(${LIST_HEIGHT} - 59px)`,
          }}
        >
          <Stack sx={{ gap: 1 }}>
            {records?.map((vendor) =>
              isAlternateVersionEnabled ? (
                <AlternateVendorCard
                  key={vendor.id}
                  vendor={vendor as IContactGroupAddress}
                  isSelected={selectedVendorId === vendor.id}
                  onVendorInfo={onVendorInfo}
                />
              ) : (
                <LegacyVendorCard key={vendor.id} vendor={vendor as IVendor} />
              )
            )}
          </Stack>
        </InfiniteScroll>
      </Box>
    </Stack>
  );
};
